import LogoSvg from '@/assets/wntd-wordmark-orange.svg';

interface CelebrityMatch {
  name: string;
  matchPercentage: number;
  knownFor: string;
  imageUrl?: string;
}

interface CelebrityShareCardProps {
  imageUrl?: string;
  matches?: CelebrityMatch[];
  vibe?: string;
  userName?: string;
}

const RANK_ACCENT = ['#FF6F3B', '#94A3B8', '#64748B'];
const RANK_BG     = ['rgba(255,111,59,0.15)', 'rgba(148,163,184,0.10)', 'rgba(100,116,139,0.08)'];
const RANK_BORDER = ['rgba(255,111,59,0.30)', 'rgba(148,163,184,0.18)', 'rgba(100,116,139,0.12)'];
const RANK_LABELS = ['#1 Match', '#2 Match', '#3 Match'];

export function CelebrityShareCard({ imageUrl, matches = [], vibe, userName }: CelebrityShareCardProps) {
  const topMatches = matches.slice(0, 3);

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#0a0a0a',
        padding: '48px 48px 40px',
      }}
    >
      {/* Hero — user selfie — taller for 9:16 */}
      <div
        style={{
          height: 780,
          flexShrink: 0,
          borderRadius: 32,
          overflow: 'hidden',
          position: 'relative',
          backgroundColor: '#1a1a1a',
          marginBottom: 24,
        }}
      >
        {imageUrl ? (
          <img
            src={imageUrl}
            alt="Your photo"
            crossOrigin="anonymous"
            style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center top' }}
          />
        ) : (
          <div
            style={{
              width: '100%',
              height: '100%',
              background: 'linear-gradient(135deg, #FF5C4D 0%, #FF6F3B 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 80,
            }}
          >
            ✨
          </div>
        )}

        {/* Dark gradient scrim */}
        <div
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: 200,
            background: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, transparent 100%)',
          }}
        />

        {/* Top badge */}
        <div
          style={{
            position: 'absolute',
            top: 24,
            left: 24,
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '8px 18px',
            borderRadius: 40,
            backgroundColor: 'rgba(0,0,0,0.55)',
            border: '1px solid rgba(255,255,255,0.12)',
          }}
        >
          <span style={{ fontSize: 20 }}>✨</span>
          <span style={{ fontSize: 20, fontWeight: 600, color: 'white' }}>Celebrity Match</span>
        </div>

        {/* Overlaid text at bottom */}
        <div
          style={{
            position: 'absolute',
            bottom: 24,
            left: 28,
            right: 28,
          }}
        >
          <p style={{ fontSize: 28, fontWeight: 700, color: 'white', marginBottom: 4 }}>
            Your Celebrity Twins
          </p>
          {vibe && (
            <p style={{ fontSize: 22, color: '#FF6F3B', fontWeight: 500 }}>{vibe}</p>
          )}
        </div>
      </div>

      {/* Match rows */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 14 }}>
        {topMatches.map((match, i) => (
          <div
            key={i}
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 20,
              padding: '0 24px',
              borderRadius: 24,
              backgroundColor: RANK_BG[i],
              border: `1px solid ${RANK_BORDER[i]}`,
            }}
          >
            {/* Celebrity photo */}
            <div style={{ position: 'relative', flexShrink: 0 }}>
              <div
                style={{
                  width: 72,
                  height: 72,
                  borderRadius: '50%',
                  overflow: 'hidden',
                  border: `2.5px solid ${RANK_ACCENT[i]}40`,
                  backgroundColor: '#1a1a1a',
                }}
              >
                {match.imageUrl ? (
                  <img
                    src={match.imageUrl}
                    alt={match.name}
                    crossOrigin="anonymous"
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                ) : (
                  <div
                    style={{
                      width: '100%',
                      height: '100%',
                      background: `linear-gradient(135deg, ${RANK_ACCENT[i]}25, ${RANK_ACCENT[i]}08)`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 24,
                    }}
                  >
                    ⭐
                  </div>
                )}
              </div>
              {/* Rank pill */}
              <div
                style={{
                  position: 'absolute',
                  bottom: -6,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  padding: '2px 10px',
                  borderRadius: 10,
                  backgroundColor: RANK_ACCENT[i],
                  fontSize: 13,
                  fontWeight: 700,
                  color: i === 0 ? '#fff' : '#0a0a0a',
                  whiteSpace: 'nowrap',
                }}
              >
                {RANK_LABELS[i]}
              </div>
            </div>

            {/* Name + info */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <p
                style={{
                  fontSize: 26,
                  fontWeight: 700,
                  color: 'white',
                  lineHeight: 1.2,
                  marginBottom: 4,
                  overflow: 'hidden',
                  display: '-webkit-box',
                  WebkitLineClamp: 1,
                  WebkitBoxOrient: 'vertical',
                }}
              >
                {match.name}
              </p>
              <p
                style={{
                  fontSize: 18,
                  color: 'rgba(255,255,255,0.45)',
                  lineHeight: 1.3,
                  overflow: 'hidden',
                  display: '-webkit-box',
                  WebkitLineClamp: 1,
                  WebkitBoxOrient: 'vertical',
                }}
              >
                {match.knownFor}
              </p>
            </div>

            {/* Percentage */}
            <div style={{ flexShrink: 0, textAlign: 'center' }}>
              <span
                style={{
                  fontSize: 38,
                  fontWeight: 900,
                  color: RANK_ACCENT[i],
                  lineHeight: 1,
                  display: 'block',
                }}
              >
                {match.matchPercentage}
              </span>
              <span style={{ fontSize: 16, fontWeight: 600, color: `${RANK_ACCENT[i]}70` }}>%</span>
            </div>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingTop: 28,
          flexShrink: 0,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <img src={LogoSvg} alt="WNTD" style={{ height: 36 }} />
          <span style={{ fontSize: 22, color: 'rgba(255,255,255,0.4)', fontWeight: 500 }}>
            Celebrity Match
          </span>
        </div>
        <span style={{ fontSize: 22, color: 'rgba(255,255,255,0.3)' }}>wntd.ai</span>
      </div>
    </div>
  );
}
